/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, {
  IresponseData,
  IresponseDataPaginated,
  ImportResult,
  fetchExportBlob,
} from "@/lib/api";
import { Subscription } from "./subscription";
import { Payment } from "./payment";
import { StreamingService } from "./streaming-service";

export enum ESupplierStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export interface Supplier {
  id: number;
  name: string;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  status: ESupplierStatus;
  score?: number
  createdAt: string;
  updatedAt?: string | null;
  subscriptions?: Subscription[]
  payments?: Payment[]
  supplierServices?: SupplierService[]
}

export interface CreateSupplierRequest {
  name: string;
  phone?: string;
  email?: string;
  notes?: string;
  score?: number
}

export interface UpdateSupplierRequest {
  name?: string;
  phone?: string;
  email?: string;
  notes?: string;
  status?: ESupplierStatus;
  score?: number
}

export interface SupplierFilters {
  search?: string;
  status?: ESupplierStatus;
  page?: number;
  limit?: number;
}

// Export / Import
export interface SupplierExportFilters {
  search?: string;
  status?: ESupplierStatus;
}

export interface ImportSuppliersRequest {
  file: string; // Excel en base64
  updated: boolean;
}

export interface SupplierPricePlan {
  id: number;
  name: string;
  price: number;
  duration: number;
  status: ESupplierStatus;
  isDefault: boolean;
  pricePlanType: "SLOT" | "FULL_ACCOUNT";
  ownerType?: "CLIENT" | "SUPPLIER";
  supplierId?: number;
  streamingServiceId?: number;
}

export interface CreateSupplierPricePlanRequest {
  price: number;
  name: string;
  status?: ESupplierStatus;
  isDefault?: boolean;
  pricePlanType: "SLOT" | "FULL_ACCOUNT";
  duration: number;
  ownerType?: "CLIENT" | "SUPPLIER";
  supplierId?: number;
  streamingServiceId?: number;
}

export interface SupplierService {
  id: number;
  supplierId: number;
  serviceId: number;
  status: ESupplierStatus;
  service: StreamingService
}

export interface CreateSupplierServiceRequest {
  serviceId: number;
  status?: ESupplierStatus;
}

export interface UpdateSupplierServiceRequest {
  status?: ESupplierStatus;
}

function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

export const supplierService = {
  async create(data: CreateSupplierRequest): Promise<IresponseData<Supplier>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.post<IresponseData<Supplier>>(
        "/suppliers",
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  async getAll(
    filters?: SupplierFilters,
  ): Promise<IresponseDataPaginated<Supplier[]>> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<IresponseDataPaginated<Supplier[]>>(
        "/suppliers",
        { params: cleanFilters },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener los proveedores. Por favor, intenta de nuevo.",
      );
    }
  },

  async getOne(id: number): Promise<IresponseData<Supplier>> {
    try {
      const response = await apiClient.get<IresponseData<Supplier>>(
        `/suppliers/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener el proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  async update(
    id: number,
    data: UpdateSupplierRequest,
  ): Promise<IresponseData<Supplier>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.put<IresponseData<Supplier>>(
        `/suppliers/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar el proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  async createPricePlan(
    supplierId: number,
    data: CreateSupplierPricePlanRequest,
  ): Promise<IresponseData<SupplierPricePlan>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.post<IresponseData<SupplierPricePlan>>(
        `/suppliers/${supplierId}/price-plans`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el plan de precio del proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  async getPricePlans(
    supplierId: number,
  ): Promise<IresponseDataPaginated<SupplierPricePlan[]>> {
    try {
      const response = await apiClient.get<
        IresponseDataPaginated<SupplierPricePlan[]>
      >(`/suppliers/${supplierId}/price-plans`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener los planes de precio del proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  async addService(
    supplierId: number,
    data: CreateSupplierServiceRequest,
  ): Promise<IresponseData<SupplierService>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.post<IresponseData<SupplierService>>(
        `/suppliers/${supplierId}/services`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al agregar el servicio al proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  async getServices(
    supplierId: number,
  ): Promise<IresponseDataPaginated<SupplierService[]>> {
    try {
      const response = await apiClient.get<
        IresponseDataPaginated<SupplierService[]>
      >(`/suppliers/${supplierId}/services`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener los servicios del proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  async updateService(
    supplierId: number,
    id: number,
    data: UpdateSupplierServiceRequest,
  ): Promise<IresponseData<SupplierService>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.put<IresponseData<SupplierService>>(
        `/suppliers/${supplierId}/services/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar el servicio del proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Exportar proveedores a un archivo (Excel)
   */
  async exportSuppliers(
    filters?: SupplierExportFilters,
  ): Promise<{ blob: Blob; fileName: string }> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      return await fetchExportBlob(
        "/suppliers/export",
        cleanFilters,
        `proveedores-export-${Date.now()}.xlsx`,
      );
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al exportar los proveedores. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Importar proveedores desde un archivo Excel (base64)
   */
  async importSuppliers(
    data: ImportSuppliersRequest,
  ): Promise<IresponseData<ImportResult>> {
    try {
      const response = await apiClient.post<IresponseData<ImportResult>>(
        "/suppliers/import",
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al importar los proveedores. Por favor, intenta de nuevo.",
      );
    }
  },
};
